import React from 'react';
import cn from 'classnames';
import BootstrapTable from 'react-bootstrap-table-next';
import * as _ from 'lodash';
import styles from './OrderFulfillmentsTable.module.scss';
import { getShippingMethod } from '../../../utils/orderHelper';
import { convertedCurrencyFormat, stringOrNAFormatter } from '../../../utils/formatHelper';
import Button from '../../common/Button';
import StatusBadge from '../../common/StatusBadge';
import {
  FULFILLMENT_STATE_CANCELED,
  FULFILLMENT_STATE_COMPLETED,
  FULFILLMENT_STATE_REJECTED,
  FULFILLMENT_STATE_CSR,
  FULFILLMENT_STATE_WAITING_FOR_SHIPPING,
  RETAILER_STATE_ORDERED,
} from '../../../constants/orderStates';
import { upperFirst } from 'lodash';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { addDiscountAmountToOrdersReportAndApp } from '../../../constants/featureFlag';
import { useSelector } from 'react-redux';

function getFulfillmentLineIdByVariantId(fulfillment, variantId) {
  const fulfillmentLine = fulfillment.fulfillment_lines.find((fl) => fl.variant_id === variantId);
  return fulfillmentLine.id;
}

function calculateFulfillmentsRows(fulfillments) {
  return fulfillments.map((fulfillment) => {
    const {
      id,
      tracking_number,
      fulfillment_lines: lines,
      fulfillment_shipments: shipments,
      fulfillment_partial_rejections: rejections,
      fulfillment_returns: returns,
    } = fulfillment;
    const status = fulfillment.currentState.state;
    const fulfillmentShippingMethod = getShippingMethod(fulfillment);

    const data = {
      id,
      status,
      pick_ticket: fulfillment.id_in_source,
      warehouse_name: fulfillment.warehouse.name,
    };

    const rowsByFulfillmentLineId = {};

    lines.forEach((fulfillmentLine) => {
      rowsByFulfillmentLineId[fulfillmentLine.id] = {
        id: fulfillmentLine.id,
        variant_id: fulfillmentLine.variant_id,
        title: fulfillmentLine.variant.title,
        sku: fulfillmentLine.variant.sku,
        unit_price: fulfillmentLine.total_line_price,
        ordered: fulfillmentLine.original_quantity || fulfillmentLine.quantity,
        shipped: 0,
        returned: 0,
        rejected: 0,
        shipping_method: [],
        tracking_number: [],
        return_tracking_number: [],
        discount_amount: fulfillmentLine.discount_amount || 0,
      };
    });

    shipments.forEach((fulfillmentShipment) => {
      const trackingNumber = fulfillmentShipment.tracking_number || tracking_number;
      const shippingMethod = getShippingMethod(fulfillmentShipment) || fulfillmentShippingMethod;
      const returnTrackingNumber = fulfillmentShipment.return_tracking_number;

      fulfillmentShipment.fulfillment_shipment_lines.forEach(({ id, variant_id, quantity }) => {
        const fulfillmentLineId = getFulfillmentLineIdByVariantId(fulfillment, variant_id);
        rowsByFulfillmentLineId[fulfillmentLineId].id = `shipment-line-${id}`;
        rowsByFulfillmentLineId[fulfillmentLineId].shipped += quantity;
        rowsByFulfillmentLineId[fulfillmentLineId].shipping_method.push(shippingMethod);
        if (trackingNumber) {
          rowsByFulfillmentLineId[fulfillmentLineId].tracking_number.push(trackingNumber);
        }
        if (returnTrackingNumber) {
          rowsByFulfillmentLineId[fulfillmentLineId].return_tracking_number.push(returnTrackingNumber);
        }
      });
    });

    if (rejections.length > 0) {
      rejections.forEach(({ fulfillment_partial_reject_lines }) => {
        fulfillment_partial_reject_lines.forEach(({ id, variant_id, quantity }) => {
          const fulfillmentLineId = getFulfillmentLineIdByVariantId(fulfillment, variant_id);
          rowsByFulfillmentLineId[fulfillmentLineId].rejected += quantity;
        });
      });
    } else {
      lines.forEach((fulfillmentLine) => {
        const qtyToIncrease = [FULFILLMENT_STATE_CANCELED, FULFILLMENT_STATE_REJECTED].some((state) => state === status)
          ? rowsByFulfillmentLineId[fulfillmentLine.id].ordered
          : 0;
        rowsByFulfillmentLineId[fulfillmentLine.id].rejected += qtyToIncrease;
      });
    }

    returns.forEach(({ fulfillment_return_lines }) => {
      fulfillment_return_lines.forEach(({ fulfillment_line_id, quantity, status }) => {
        rowsByFulfillmentLineId[fulfillment_line_id].returned += quantity;
        rowsByFulfillmentLineId[fulfillment_line_id].returned_status = upperFirst(status);
      });
    });

    data.rows = Object.values(rowsByFulfillmentLineId);
    return data;
  });
}

function OrderFulfillmentsTable(props) {
  const supplierId = useSelector((state) => state.role.supplier.id);
  let suppliersToAddDiscountAmount = [];

  const isDiscountAmountFeatureEnabledForAll = useFeatureFlag(addDiscountAmountToOrdersReportAndApp, false, true);
  const discountAmountFeatureValue = useFeatureFlag(addDiscountAmountToOrdersReportAndApp, []);

  try {
    suppliersToAddDiscountAmount = JSON.parse(discountAmountFeatureValue);
  } catch {
    suppliersToAddDiscountAmount = [];
  }

  const shouldShowDiscountAmount =
    isDiscountAmountFeatureEnabledForAll || suppliersToAddDiscountAmount.includes(supplierId);

  if (!props.order || !props.order.fulfillments.length) {
    return null;
  }

  const order = props.order;
  const { currency, conversion_rate, order_exchange_rates } = order;

  const calculateTotalCost = (cell, row) => {
    const quantity = (row.id.toString().includes('shipment-line') ? row.shipped : row.ordered) - row.rejected;
    return convertedCurrencyFormat(row.unit_price * quantity, currency, conversion_rate, order_exchange_rates);
  };

  const fulfillments = calculateFulfillmentsRows(order.fulfillments);
  const canCancelFulfillments = order.fulfillments.filter((fulfillment) => {
    if (order.expired || order.current_retailer_state.state !== RETAILER_STATE_ORDERED) {
      return false;
    }
    const { state } = fulfillment.currentState;
    return state === FULFILLMENT_STATE_WAITING_FOR_SHIPPING || state === FULFILLMENT_STATE_CSR;
  });

  const hasPackingSlips = order.fulfillments.filter((fulfillment) => fulfillment.packing_slip_link);

  return (
    <div className={styles.fulfillments}>
      <hr />
      <h3>Fulfillments</h3>

      {fulfillments.map((fulfillment) => {
        const isCompleted = fulfillment.status === FULFILLMENT_STATE_COMPLETED;
        const canCancelFulfillment =
          order.fulfillments.length > 1 && canCancelFulfillments.find(({ id }) => id === fulfillment.id);
        const hasPackingSlip = order.fulfillments.length > 1 && hasPackingSlips.find(({ id }) => id === fulfillment.id);
        const hasRejectedColumn = fulfillment.rows.some((row) => row.rejected > 0);
        const hasReturnedColumn = isCompleted && fulfillment.rows.some((row) => row.returned > 0);
        const hasReturnTrackingNumber = fulfillment.rows.some((row) => !!row.return_tracking_number.length);

        const columns = [
          {
            text: 'Title',
            dataField: 'title',
          },
          {
            text: 'SKU',
            dataField: 'sku',
          },
          {
            text: 'Unit Price',
            dataField: 'unit_price',
            formatter: (cell) => convertedCurrencyFormat(cell, currency, conversion_rate, order_exchange_rates),
          },
          {
            text: 'Ordered',
            dataField: 'ordered',
          },
          {
            text: 'Shipped',
            dataField: 'shipped',
          },
        ];

        if (hasReturnedColumn) {
          columns.push({
            text: 'Returned',
            dataField: 'returned',
          });
        }

        if (hasRejectedColumn) {
          columns.push({
            text: 'Canceled',
            dataField: 'rejected',
          });
        }

        columns.push(
          {
            text: 'Total Value',
            dataField: 'total_value',
            formatter: calculateTotalCost,
            isDummyField: true,
          },
          {
            text: 'Tracking Number',
            dataField: 'tracking_number',
            formatter: (cell) => _.uniq(cell).join(', '),
          },
          {
            text: 'Carrier',
            dataField: 'shipping_method',
            formatter: (cell) => _.uniq(cell).join(', '),
          },
        );
        if (hasReturnedColumn) {
          columns.push({
            text: 'Return Status',
            dataField: 'returned_status',
          });
        }

        if (hasReturnTrackingNumber) {
          columns.push({
            text: 'Return tracking number',
            dataField: 'return_tracking_number',
            formatter: (cell) => _.uniq(cell).join(', '),
          });
        }

        if (shouldShowDiscountAmount) {
          const totalValueIndex = columns.findIndex(({ dataField }) => dataField === 'total_value');
          columns.splice(totalValueIndex + 1, 0, {
            text: 'Discount',
            dataField: 'discount_amount',
            formatter: (cell) => convertedCurrencyFormat(cell, currency, conversion_rate, order_exchange_rates),
          });
        }

        return (
          <div
            className={styles.fulfillmentWrapper}
            key={fulfillment.id}
            data-testid={`fulfillment-wrapper-${fulfillment.id}`}
          >
            <h4 data-testid={`fulfillment-${fulfillment.id}-title`}>
              <span>{fulfillment.warehouse_name} Warehouse</span>
              {hasPackingSlip && (
                <a
                  href={hasPackingSlip.packing_slip_link}
                  target="_blank"
                  className={cn('link-button', styles.actionButton)}
                  rel="noopener noreferrer"
                >
                  <Button text="Download Packing Slip" />
                </a>
              )}
            </h4>

            <div className={styles.list}>
              <dl>
                <dt>Pick Ticket:</dt>
                <dd>{stringOrNAFormatter(fulfillment.pick_ticket)}</dd>
              </dl>
              <dl>
                <dt>Fulfillment Status:</dt>
                <dd>
                  <StatusBadge status={fulfillment.status} id={`order_fulfillment_state_${fulfillment.id}`} />
                </dd>
              </dl>
              {hasReturnedColumn && order.post_shipment_status && (
                <dl>
                  <dt>Return status:</dt>
                  <dd>
                    <StatusBadge
                      status={order.post_shipment_status}
                      id={`order_return_state_${fulfillment.id}`}
                      postShipmentStatus
                    />
                  </dd>
                </dl>
              )}

              {canCancelFulfillment && (
                <Button
                  color="white"
                  text="Cancel Fulfillment"
                  onClick={() => props.onCancel(fulfillment.id)}
                  className={styles.actionButton}
                />
              )}
            </div>

            <BootstrapTable
              data={fulfillment.rows}
              columns={columns}
              keyField="id"
              striped
              bordered={false}
              hover
            ></BootstrapTable>
          </div>
        );
      })}
    </div>
  );
}

export default OrderFulfillmentsTable;
